import EmblaCarousel from 'embla-carousel';

import './view.scss';

function initAnalystRecognition(root: HTMLElement): void {
  const viewport = root.querySelector<HTMLElement>('[data-analyst-recognition-viewport]');
  const container = viewport?.querySelector<HTMLElement>('.embla__container');
  if (!viewport || !container) return;

  const prevBtn = root.querySelector<HTMLButtonElement>('[data-carousel-prev]');
  const nextBtn = root.querySelector<HTMLButtonElement>('[data-carousel-next]');

  const slides = Array.from(container.children).filter((el) =>
    el.classList.contains('analyst-recognition__card'),
  ) as HTMLElement[];

  if (slides.length <= 1) {
    if (prevBtn) prevBtn.style.display = 'none';
    if (nextBtn) nextBtn.style.display = 'none';
    slides[0]?.classList.add('is-active');
    return;
  }

  // Mirrors $breakpoint-mobile in responsive.mixin.scss, where the side arrows step aside.
  const mobile = window.matchMedia('(max-width: 768px)');
  const carousel = root.querySelector<HTMLElement>('.analyst-recognition__carousel');
  let mobileNavBuilt = false;

  const buildMobileNav = () => {
    if (mobileNavBuilt || !prevBtn || !nextBtn || !carousel) return;
    mobileNavBuilt = true;

    const nav = document.createElement('div');
    nav.className = 'analyst-recognition__mobile-nav';

    const counter = document.createElement('span');
    counter.className = 'analyst-recognition__mobile-nav-count';
    counter.dataset.carouselCounter = 'true';
    counter.setAttribute('aria-hidden', 'true');

    const copies = [prevBtn, nextBtn].map((source) => {
      const btn = source.cloneNode(true) as HTMLButtonElement;
      btn.classList.remove(
        'analyst-recognition__nav',
        'analyst-recognition__nav--prev',
        'analyst-recognition__nav--next',
      );
      btn.classList.add('analyst-recognition__mobile-nav-btn');
      return btn;
    });

    nav.append(copies[0], counter, copies[1]);
    carousel.appendChild(nav);
  };

  if (mobile.matches) buildMobileNav();

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const embla = EmblaCarousel(viewport, {
    loop: true,
    align: 'center',
    startIndex: 0,
  });

  // Only the centred card is exposed to AT and the tab order; the arrows reach the rest.
  const setActive = () => {
    const selected = embla.selectedScrollSnap();
    const counterLabel = `${selected + 1} / ${slides.length}`;
    root
      .querySelectorAll<HTMLElement>('[data-carousel-counter]')
      .forEach((el) => (el.textContent = counterLabel));
    slides.forEach((slide, index) => {
      const isSel = index === selected;
      slide.classList.toggle('is-active', isSel);
      if (isSel) slide.removeAttribute('aria-hidden');
      else slide.setAttribute('aria-hidden', 'true');
      slide
        .querySelectorAll<HTMLElement>('a, button, [tabindex]')
        .forEach((el) => el.setAttribute('tabindex', isSel ? '0' : '-1'));
    });
  };

  embla.on('select', setActive);
  embla.on('reInit', setActive);
  setActive();

  root.addEventListener('click', (event) => {
    const target = event.target as HTMLElement | null;
    if (target?.closest('[data-carousel-prev]')) embla.scrollPrev(reducedMotion.matches);
    else if (target?.closest('[data-carousel-next]')) embla.scrollNext(reducedMotion.matches);
  });

  // Only entering the mobile width after load needs the row; leaving it never does.
  mobile.addEventListener('change', () => {
    if (!mobile.matches || mobileNavBuilt) return;
    buildMobileNav();
    setActive();
  });

  let resizeTimer: number | undefined;
  window.addEventListener('resize', () => {
    if (resizeTimer !== undefined) window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => embla.reInit(), 150);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  document
    .querySelectorAll<HTMLElement>('[data-analyst-recognition]')
    .forEach(initAnalystRecognition);
});
